'use client';

import { useEffect, useRef, useState } from 'react';
import { fetchClientFileBlob } from '@/app/(main)/client-detail/utils/downloadClientFile';
import { Spinner } from '@/app/components/Spinner';

interface ProxiedPdfPreviewProps {
  fileUrl: string;
  filename: string;
  width: number;
  height: number;
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * PDF preview for client documents. Direct iframe src to S3/legacy URLs is often
 * blocked (X-Frame-Options / CSP). We load the file through /api/gallery/download
 * and show it via a same-origin blob: URL in the iframe.
 */
export const ProxiedPdfPreview = ({ fileUrl, filename, width, height, onLoad, onError }: ProxiedPdfPreviewProps) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    setBlobUrl(null);

    if (!fileUrl) {
      setLoadError(true);
      onError?.();
      return () => {
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }
      };
    }

    const load = async () => {
      try {
        const blob = await fetchClientFileBlob(fileUrl, filename);
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = objectUrl;
        setBlobUrl(objectUrl);
      } catch {
        if (!cancelled) {
          setLoadError(true);
          onError?.();
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [fileUrl, filename]);  

  if (loadError) {
    return (
      <div
        className="flex justify-center items-center rounded-[13px] bg-grey-100 text-body-regular text-grey-700 px-4 text-center"
        style={{ width, height }}
      >
        Could not load PDF preview
      </div>
    );
  }

  if (!blobUrl) {
    return (
      <div className="flex justify-center items-center rounded-[13px] bg-grey-100" style={{ width, height }}>
        <Spinner />
      </div>
    );
  }

  return (
    <iframe
      src={blobUrl}
      width={width}
      height={height}
      allowFullScreen
      onLoad={onLoad}
      className="rounded-[13px] max-h-[420px] max-w-[420px] border-0"
      title={filename}
    />
  );
};
